// @flow
import styles from './RecommendedJobsDashboard.less';
import commonStyles from '../SignedInDashboardCommon.less';

import { connect } from 'react-redux';
import { isUndefined, get } from 'lodash';
import React, { Component } from 'react';

import { locationFromHref } from '@seek/seek-jobs-seo/build/location';
import {
  ScreenReaderOnly,
  TextLink,
  ThumbsUpIcon,
  Text
} from 'seek-style-guide/react';
import DashboardHeading from '../DashboardHeading/DashboardHeading';
import DashboardSectionError from '../DashboardSectionError/DashboardSectionError';
import DashboardItem from '../DashboardItem/DashboardItem';
import NavLink from 'src/components/NavLink/NavLink';
import ViewAllLink from '../ViewAllLink/ViewAllLink';

import { getDashboard } from 'src/store/dashboard/dashboard';

import {
  trackRecommendedJobClicked,
  trackRecommendedJobsViewed
} from 'src/modules/job-tracking';

type JobTypes = {
  id: number,
  title: string,
  advertiser: string,
  isNew: boolean,
  order: number
};

type Props = {
  jobs?: Array<JobTypes>,
  batchId?: number,
  isNewBatch?: boolean,
  totalCount?: number,
  seekerId?: number,
  sessionId?: string,
  retryRecommendations: Function
};

type State = {|
  isViewedEventSent?: boolean
|};

const mapStateToProps = ({
  dashboard: { recommendedJobs },
  user: { seekerId, sessionId }
}) => ({
  jobs: get(recommendedJobs, 'data'),
  totalCount: get(recommendedJobs, 'totalCount'),
  batchId: get(recommendedJobs, 'batchId'),
  isNewBatch: get(recommendedJobs, 'isNewBatch'),
  seekerId,
  sessionId
});

const mapDispatchToProps = dispatch => ({
  retryRecommendations: () =>
    dispatch(getDashboard({ sections: 'recommendedJobs' }))
});

class RecommendedJobsDashboard extends Component<Props, State> {
  state = {
    isViewedEventSent: false
  };

  componentWillReceiveProps(nextProps: Props) {
    const { jobs, batchId, isNewBatch, seekerId, sessionId } = nextProps;

    if (
      batchId &&
      seekerId &&
      !isUndefined(jobs) &&
      !this.state.isViewedEventSent &&
      sessionId
    ) {
      trackRecommendedJobsViewed({
        jobs,
        batchId,
        isNewBatch,
        seekerId,
        sessionId,
        view: 'homePage'
      });
      this.setState({ isViewedEventSent: true });
    }
  }

  onJobClick = (job: JobTypes) => {
    const { seekerId, batchId, sessionId } = this.props;
    trackRecommendedJobClicked({
      job,
      seekerId,
      sessionId,
      batchId,
      view: 'homePage'
    });
  };

  renderJobItem = (job: JobTypes) => {
    const { id, title, advertiser, isNew } = job;
    /* eslint-disable react/jsx-no-bind */
    return (
      <DashboardItem
        key={id}
        heading={() => (
          <span>
            {isNew && (
              <span
                data-automation="dashboardRecommendedJobsNew"
                className={styles.isNew}
              >
                New<ScreenReaderOnly>recommendation</ScreenReaderOnly>
                <span>{' — '}</span>
              </span>
            )}
            <NavLink
              analytics="dashboard-recommended-job-click"
              children={
                <span>
                  {title} <ScreenReaderOnly>. At {advertiser}</ScreenReaderOnly>
                </span>
              }
              component={TextLink}
              data-automation="recommendedJobHeadingLink"
              location={locationFromHref(`/job/${id}`)}
              onClick={() => this.onJobClick(job)}
            />
          </span>
        )}
        subheading={() => (
          <span aria-hidden="true" data-automation="recommendedJobAdvertiser">
            {advertiser}
          </span>
        )}
      />
    );
    /* eslint-enable react/jsx-no-bind */
  };

  render() {
    const { jobs, totalCount, retryRecommendations } = this.props;
    const label = `Recommended job${totalCount === 1 ? '' : 's'}`;

    const viewAllLinkProps = {
      analytics: 'dashboard-all-recommended-jobs-click',
      location: '/recommended'
    };

    return (
      <nav
        aria-label={label}
        role="navigation"
        data-automation="recommendedJobsDashboard"
      >
        <DashboardHeading
          label={label}
          count={totalCount}
          Icon={ThumbsUpIcon}
          iconClass={styles.icon}
          className={styles.heading}
          href="/recommended"
          analytics="dashboard-heading-recommended-jobs-click"
        />
        {isUndefined(jobs) ? (
          <DashboardSectionError
            dataAutomation="recommendedJobSectionError"
            label={label}
            navLinkProps={viewAllLinkProps}
            retryHandler={retryRecommendations}
          />
        ) : (
          <div>
            {jobs && jobs.length > 0 ? (
              <div className={commonStyles.items}>
                {jobs.map(this.renderJobItem)}
                <ViewAllLink
                  {...viewAllLinkProps}
                  dataAutomation="recommendedJobsViewAll"
                  screenReaderText="recommended jobs"
                />
              </div>
            ) : (
              <Text secondary small className={commonStyles.noItems}>
                Complete&nbsp;
                <NavLink
                  analytics="dashboard-recommended-jobs-profile"
                  children="your profile"
                  component={TextLink}
                  location="/profile/"
                />
                &nbsp;to see personalised job recommendations here.
              </Text>
            )}
          </div>
        )}
      </nav>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RecommendedJobsDashboard);
export { RecommendedJobsDashboard as RecommendedJobsDashboardForTest };
